import React, { useCallback, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import {
  MotifAccordion,
  MotifAccordionTrigger,
  MotifAccordionContent,
  MotifToast,
} from "@ey-xd/motif-react";
import { OrdersService } from "../../../api/services/orders.service";
import { ChatNewIcon } from "../../../assets/Icons/ChatLines";
import { ChatPlusIcon } from "../../../assets/Icons/ChatPlusIn";
import { Tooltip } from "../../CommonUIComponents/Tooltip/Tooltip";
import {
  getChecklistQuestions,
  getChecklistQuestionId,
  getQuestionsMissingNoComments,
  groupChecklistQuestions,
} from "./checklist.utils";
import "./CheckListTab.scss";

const ANSWER_OPTIONS = [
  { value: "Yes", label: "Yes" },
  { value: "No", label: "NO" },
  { value: "NA", label: "NA" },
];

// Maps already-submitted responses/comments returned alongside the questions
const buildInitialAnswers = (rawQuestions) => {
  const initialAnswers = {};

  rawQuestions.forEach((question, index) => {
    const questionId = getChecklistQuestionId(question, index);
    const response =
      question.response ??
      question.answer ??
      question.selectedResponse ??
      question.savedResponse ??
      "";
    const comment =
      question.comment ?? question.savedComment ?? question.remarks ?? "";

    if (response || comment) {
      initialAnswers[questionId] = { answer: response, comment };
    }
  });

  return initialAnswers;
};

const CheckListTab = ({
  isEditing,
  orderId,
  analyser,
  analyzerType,
  status,
  checklistRole,
  accordionTitle,
  submitRef,
  resetRef,
}) => {
  const [questions, setQuestions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [answers, setAnswers] = useState({});
  // Last-saved answers/comments, used to discard unsaved edits on cancel
  const [savedAnswers, setSavedAnswers] = useState({});
  const [commentQuestion, setCommentQuestion] = useState(null);
  const [commentDraft, setCommentDraft] = useState("");
  const [validationToast, setValidationToast] = useState("");

  useEffect(() => {
    let isActive = true;

    const fetchChecklist = async () => {
      if (!orderId || !analyser || !checklistRole) {
        setError("Checklist details are unavailable for this order.");
        return;
      }

      setIsLoading(true);
      setError("");

      try {
        const roleQueryParam = ["Preparer", "Reviewer"].includes(
          checklistRole,
        )
          ? checklistRole
          : undefined;
        const response = await OrdersService.getChecklistQuestions(
          analyser,
          orderId,
          roleQueryParam,
        );

        if (isActive) {
          const nextQuestions = getChecklistQuestions(response);
          const initialAnswers = buildInitialAnswers(nextQuestions);
          setQuestions(nextQuestions);
          setAnswers(initialAnswers);
          setSavedAnswers(initialAnswers);
        }
      } catch (requestError) {
        console.error("Failed to fetch checklist questions:", requestError);
        if (isActive) {
          setError("Unable to load checklist items.");
        }
      } finally {
        if (isActive) {
          setIsLoading(false);
        }
      }
    };

    fetchChecklist();

    return () => {
      isActive = false;
    };
  }, [analyser, checklistRole, orderId]);

  const displayQuestions = questions.map((question, index) => ({
    ...question,
    displayNumber: question.slNo ?? index + 1,
    questionId: getChecklistQuestionId(question, index),
  }));
  const checklistGroups = groupChecklistQuestions(displayQuestions);

  const handleAnswerChange = (questionId, answer) => {
    if (!isEditing) {
      return;
    }

    setAnswers((previous) => ({
      ...previous,
      [questionId]: {
        ...(previous[questionId] || {}),
        answer,
      },
    }));
    setValidationToast("");
  };

  const openCommentDialog = (question) => {
    if (!isEditing) {
      return;
    }

    const questionId = question.questionId;
    setCommentQuestion(question);
    setCommentDraft(answers[questionId]?.comment || "");
  };

  const closeCommentDialog = () => {
    setCommentQuestion(null);
    setCommentDraft("");
  };

  const saveComment = () => {
    if (!commentQuestion) {
      return;
    }

    setAnswers((previous) => ({
      ...previous,
      [commentQuestion.questionId]: {
        ...(previous[commentQuestion.questionId] || {}),
        comment: commentDraft,
      },
    }));
    setValidationToast("");
    closeCommentDialog();
  };

  const handleSubmit = useCallback(async () => {
    if (!isEditing || !orderId || !analyzerType || !status) {
      setError("Checklist details are unavailable for submission.");
      return false;
    }

    const questionsMissingComments = getQuestionsMissingNoComments(
      displayQuestions,
      answers,
    );
    if (questionsMissingComments.length) {
      const questionNumbers = questionsMissingComments
        .map((question) => question.slNo ?? question.displayNumber)
        .join(", ");
      setValidationToast(
        `Comments are mandatory for answering 'NO'. Question No(s): ${questionNumbers}`,
      );
      return false;
    }
    setValidationToast("");

    const hasUnansweredQuestion = displayQuestions.some(
      (question) => !answers[question.questionId]?.answer,
    );

    if (hasUnansweredQuestion) {
      throw new Error("Please answer all the questions before submitting");
    }

    const sectionsByStage = new Map();
    displayQuestions.forEach((question) => {
      const stage = question.stage || status;
      if (!sectionsByStage.has(stage)) {
        sectionsByStage.set(stage, { stage, responses: [] });
      }
      sectionsByStage.get(stage).responses.push({
        slNo: question.slNo ?? question.displayNumber,
        response: answers[question.questionId]?.answer || "",
        comment: answers[question.questionId]?.comment?.trim() || "",
      });
    });

    const numericOrderId = Number(orderId);
    const payload = {
      orderRequestId: Number.isNaN(numericOrderId) ? orderId : numericOrderId,
      sections: Array.from(sectionsByStage.values()),
      analyzerType,
    };

    try {
      setError("");
      await OrdersService.submitChecklist(payload);
      setSavedAnswers(answers);
      return true;
    } catch (submitError) {
      console.error("Failed to submit checklist:", submitError);
      setError("Unable to submit checklist responses.");
      return false;
    }
  }, [analyzerType, answers, displayQuestions, isEditing, orderId, status]);

  // Discards any unsaved answers/comments and closes the comment dialog
  const handleReset = useCallback(() => {
    setAnswers(savedAnswers);
    setCommentQuestion(null);
    setCommentDraft("");
    setError("");
    setValidationToast("");
  }, [savedAnswers]);

  if (submitRef) {
    submitRef.current = handleSubmit;
  }

  useEffect(() => {
    if (!submitRef) {
      return undefined;
    }

    submitRef.current = handleSubmit;

    return () => {
      submitRef.current = null;
    };
  }, [handleSubmit, submitRef]);

  useEffect(() => {
    if (!resetRef) {
      return undefined;
    }

    resetRef.current = handleReset;

    return () => {
      resetRef.current = null;
    };
  }, [handleReset, resetRef]);

  return (
    <div className="checklist-container">
      
      
      <div className="checklist-content">
        {isLoading && <p>Loading checklist items...</p>}
        {error && <p>{error}</p>}
        {validationToast &&
          createPortal(
            <div className="checklist-validation-toast">
              <MotifToast
                variant="error"
                position="top"
                onClose={() => setValidationToast("")}
              >
                {validationToast}
              </MotifToast>
            </div>,
            document.body,
          )}
        {!isLoading && !error && questions.length === 0 && (
          <p>No checklist questions found.</p>
        )}
        {!isLoading && !error && questions.length > 0 && (
          <div className="checklist-accordion-list">
            {checklistGroups.map((group) => (
              <MotifAccordion
                className="checklist-accordion"
                alignIconRight
                useChevronIcon
                key={group.key}
              >
                <MotifAccordionTrigger>
                  {group.title || accordionTitle || "Checklist"}
                </MotifAccordionTrigger>
                <MotifAccordionContent>
                  <div className="checklist-column-header">
                    <span>CHECKLIST DESCRIPTION</span>
                    <span>RESPONSE</span>
                  </div>
                  {group.questions.map((question) => {
                        const questionId = question.questionId;
                        const selectedAnswer = answers[questionId]?.answer || "";
                        const questionText =
                          question.questionText ||
                          question.description ||
                          question.question ||
                          "";
                        const CommentIcon = isEditing
                          ? ChatPlusIcon
                          : ChatNewIcon;
                        const savedComment = answers[questionId]?.comment || "";
                        const commentButton = (
                          <button
                            type="button"
                            className="checklist-comment-button"
                            aria-label={`Comments for question ${question.displayNumber}`}
                            onClick={() => openCommentDialog(question)}
                            disabled={isEditing ? false : !savedComment}
                          >
                            <CommentIcon width={20} height={20} />
                          </button>
                        );

                        return (
                          <div className="checklist-question" key={questionId}>
                            <span className="checklist-question-text">
                              {question.displayNumber}. {questionText}
                            </span>
                            <div className="checklist-response-options">
                              {ANSWER_OPTIONS.map((option) => (
                                <label className="checklist-response-option" key={option.value}>
                                  <input
                                    type="radio"
                                    name={`checklist-${questionId}`}
                                    value={option.value}
                                    checked={selectedAnswer === option.value}
                                    disabled={!isEditing}
                                    onChange={() =>
                                      handleAnswerChange(questionId, option.value)
                                    }
                                  />
                                  <span>{option.label}</span>
                                </label>
                              ))}
                              <span className="checklist-comment-anchor">
                                {!isEditing && !savedComment.trim() ? null : !isEditing ? (
                                  <Tooltip
                                    trigger={commentButton}
                                    tooltipText={savedComment}
                                    placement="top"
                                  />
                                ) : (
                                  commentButton
                                )}
                              </span>
                            </div>
                          </div>
                        );
                  })}
                </MotifAccordionContent>
              </MotifAccordion>
            ))}
          </div>
        )}
      </div>

      {commentQuestion && (
        <div className="checklist-comment-backdrop" role="presentation">
          <div
            className="checklist-comment-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="checklist-comment-title"
          >
            <div className="checklist-comment-modal-header">
              <h3 id="checklist-comment-title">Add a Comment</h3>
              <button
                type="button"
                className="checklist-comment-close"
                onClick={closeCommentDialog}
                aria-label="Close comment dialog"
              >
                ×
              </button>
            </div>
            <div className="checklist-comment-modal-body">
              <label htmlFor="checklist-comment-input">Comment</label>
              <textarea
                id="checklist-comment-input"
                value={commentDraft}
                onChange={(event) => setCommentDraft(event.target.value)}
                placeholder="Write Comments here"
              />
            </div>
            <div className="checklist-comment-modal-footer">
              <button
                type="button"
                className="checklist-comment-cancel"
                onClick={closeCommentDialog}
              >
                Cancel
              </button>
              <button
                type="button"
                className="checklist-comment-save"
                onClick={saveComment}
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CheckListTab;
